
import { memo, useState, useEffect, useRef, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faLock,
  faClipboardList,
  faDesktop,
  faChevronRight,
  faXmark,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import { Image } from '../Image';

// ==========================================
// ACCOUNT CONTAINER
// ==========================================

const StyledAccountSectionContainer = styled.section`
  width: 58px;
  height: 100%;
  margin: 0;
  padding: 0;
  border-left: 1px solid #edf0f4;
  position: relative;
  flex-shrink: 0;

  @media (max-width: 600px) {
    position: static;
    width: 48px;
    border-left: none;
  }
`;

// ==========================================
// PROFILE BUTTON
// ==========================================

const StyledAccountIconContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  button {
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;

    &:hover,
    &.active {
      background: #f1f5f9;
    }
  }
`;

const StyledProfileImageWrapper = styled.div`
  width: 36px;
  height: 36px;
  padding: 2px;
  border-radius: 50%;
  border: 2px solid #dbeafe;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    display: block;
  }

  button:hover & {
    border-color: ${({ theme }) =>
    theme.colors.primaryColor || '#2563eb'};
  }

  button.active & {
    border-color: ${({ theme }) =>
    theme.colors.primaryColor || '#2563eb'};
  }
`;

// ==========================================
// ACCOUNT POPOVER
// ==========================================

const StyledAccountContent = styled.div`
  position: absolute;
  width: 310px;
  top: calc(100% + 8px);
  right: 8px;
  background: #ffffff;
  border: 1px solid #e8edf4;
  border-radius: 14px;
  box-shadow: 0 15px 40px rgba(15, 23, 42, 0.14);
  z-index: 999999;
  overflow: hidden;
  animation: accountOpen 0.2s ease;

  @keyframes accountOpen {
    from {
      opacity: 0;
      transform: translateY(-7px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 600px) {
    position: fixed;
    width: min(310px, calc(100vw - 24px));
    top: 70px;
    right: 12px;
  }
`;

// ==========================================
// ACCOUNT HEADER
// ==========================================

const StyledAccountContentHeader = styled.div`
  position: relative;
  padding: 19px 18px 48px;
  background:
    radial-gradient(
      circle at 90% 10%,
      rgba(147, 197, 253, 0.25),
      transparent 40%
    ),
    linear-gradient(125deg, #000000, #000000);

  color: #ffffff;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.2px;
  }

  p {
    margin: 6px 0 0;
    font-size: 11px;
    color: #bfdbfe;
  }

  button {
    width: 29px;
    height: 29px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

// ==========================================
// PROFILE INFORMATION
// ==========================================

const StyledAccountProfileSection = styled.div`
  padding: 0 18px 15px;
  margin-top: -34px;
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 12px;
`;

const StyledAccountProfilePhotoContainer = styled.div`
  width: 68px;
  height: 68px;
  min-width: 68px;
  border-radius: 15px;
  padding: 4px;
  background: #ffffff;
  border: 1px solid #e8edf4;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.1);

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    border-radius: 11px;
  }
`;

const StyledAccountUserInfo = styled.div`
  min-width: 0;
  padding-bottom: 5px;

  h4 {
    font-size: 14px;
    font-weight: 750;
    color: #172033;
    margin: 0;
    overflow-wrap: anywhere;
  }

  span {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 5px;
    font-size: 10px;
    font-weight: 600;
    color: #16a34a;
  }
`;

// ==========================================
// MENU SECTION
// ==========================================

const StyledAccountContentLinks = styled.div`
  padding: 8px 10px 10px;
  border-top: 1px solid #edf0f5;
`;

const StyledAccountMenuLabel = styled.div`
  padding: 10px 9px 8px;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const StyledAccountMenuList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    margin: 0;
    padding: 0;
  }
`;

const StyledAccountMenuLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 10px;
  margin: 2px 0;
  text-decoration: none;
  border-radius: 9px;
  color: #475569;
  font-size: 13px;
  font-weight: 550;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #1e40af;
  }

  &.active {
    background: #eff6ff;
    color: #2563eb;
    font-weight: 700;

    ${'' /* Active icon styling */}
    .account-menu-icon {
      background: #dbeafe;
      color: #2563eb;
    }
  }
`;

const StyledAccountMenuIcon = styled.div`
  width: 34px;
  height: 34px;
  min-width: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 14px;
  transition: all 0.2s ease;

  ${StyledAccountMenuLink}:hover & {
    background: #e0e7ff;
    color: #2563eb;
  }
`;

const StyledAccountMenuText = styled.div`
  flex: 1;
`;

const StyledAccountMenuArrow = styled.div`
  color: #cbd5e1;
  font-size: 10px;

  ${StyledAccountMenuLink}:hover & {
    color: #2563eb;
  }
`;

// ==========================================
// ACCOUNT FOOTER
// ==========================================

const StyledAccountFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 17px;
  background: #f8fafc;
  border-top: 1px solid #edf0f5;

  svg {
    color: #16a34a;
    font-size: 13px;
  }

  span {
    font-size: 11px;
    color: #64748b;
  }
`;

// ==========================================
// ACCOUNT COMPONENT
// ==========================================

const AccountSection = memo(({ fullName, profilePhoto }) => {
  const [expandAccount, setExpandAccount] = useState(false);
  const accountRef = useRef(null);

  const imageSource = profilePhoto || '/default-profile-photo.png';

  // Toggle account menu
  const handleOnExpandAccount = (e) => {
    e.preventDefault();
    setExpandAccount((prev) => !prev);
  };

  // Close on outside click
  const closeAccountList = useCallback((e) => {
    if (
      accountRef.current &&
      !accountRef.current.contains(e.target)
    ) {
      setExpandAccount(false);
    }
  }, []);

  // Close on Escape
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') {
      setExpandAccount(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', closeAccountList, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('click', closeAccountList, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [closeAccountList, handleEscape]);

  // Close after navigating
  const handleNavigation = () => {
    setExpandAccount(false);
  };

  return (
    <StyledAccountSectionContainer ref={accountRef}>

      {/* ==================================
          PROFILE BUTTON
      ================================== */}

      <StyledAccountIconContainer>
        <button
          type="button"
          className={expandAccount ? 'active' : ''}
          onClick={handleOnExpandAccount}
          aria-label="My account"
          aria-expanded={expandAccount}
          title="My Account"
        >
          <StyledProfileImageWrapper>
            <Image
              source={imageSource}
              alt={fullName || 'Profile'}
            />
          </StyledProfileImageWrapper>
        </button>
      </StyledAccountIconContainer>

      {/* ==================================
          ACCOUNT POPOVER
      ================================== */}

      {expandAccount && (
        <StyledAccountContent>

          {/* Header */}
          <StyledAccountContentHeader>
            <div>
              <h3>My Account</h3>
              <p>Manage your account settings</p>
            </div>

            <button
              type="button"
              onClick={() => setExpandAccount(false)}
              aria-label="Close account menu"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </StyledAccountContentHeader>

          {/* User Profile */}
          <StyledAccountProfileSection>

            <StyledAccountProfilePhotoContainer>
              <Image
                source={imageSource}
                alt={fullName || 'Profile'}
              />
            </StyledAccountProfilePhotoContainer>

            <StyledAccountUserInfo>
              <h4>{fullName || 'My Account'}</h4>

              <span>
                <FontAwesomeIcon icon={faShieldHalved} />
                Secure Account
              </span>
            </StyledAccountUserInfo>

          </StyledAccountProfileSection>

          {/* Navigation Links */}
          <StyledAccountContentLinks>

            <StyledAccountMenuLabel>
              Account Settings
            </StyledAccountMenuLabel>

            <StyledAccountMenuList>

              <li>
                <StyledAccountMenuLink
                  to="/profile"
                  onClick={handleNavigation}
                >
                  <StyledAccountMenuIcon className="account-menu-icon">
                    <FontAwesomeIcon icon={faUser} />
                  </StyledAccountMenuIcon>

                  <StyledAccountMenuText>
                    My Profile
                  </StyledAccountMenuText>

                  <StyledAccountMenuArrow>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </StyledAccountMenuArrow>
                </StyledAccountMenuLink>
              </li>

              <li>
                <StyledAccountMenuLink
                  to="/change-password"
                  onClick={handleNavigation}
                >
                  <StyledAccountMenuIcon className="account-menu-icon">
                    <FontAwesomeIcon icon={faLock} />
                  </StyledAccountMenuIcon>

                  <StyledAccountMenuText>
                    Change Password
                  </StyledAccountMenuText>

                  <StyledAccountMenuArrow>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </StyledAccountMenuArrow>
                </StyledAccountMenuLink>
              </li>

              <li>
                <StyledAccountMenuLink
                  to="/logs"
                  onClick={handleNavigation}
                >
                  <StyledAccountMenuIcon className="account-menu-icon">
                    <FontAwesomeIcon icon={faClipboardList} />
                  </StyledAccountMenuIcon>

                  <StyledAccountMenuText>
                    Activity Logs
                  </StyledAccountMenuText>

                  <StyledAccountMenuArrow>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </StyledAccountMenuArrow>
                </StyledAccountMenuLink>
              </li>

              <li>
                <StyledAccountMenuLink
                  to="/sessions"
                  onClick={handleNavigation}
                >
                  <StyledAccountMenuIcon className="account-menu-icon">
                    <FontAwesomeIcon icon={faDesktop} />
                  </StyledAccountMenuIcon>

                  <StyledAccountMenuText>
                    Active Sessions
                  </StyledAccountMenuText>

                  <StyledAccountMenuArrow>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </StyledAccountMenuArrow>
                </StyledAccountMenuLink>
              </li>

            </StyledAccountMenuList>
          </StyledAccountContentLinks>

          {/* Footer */}
          {/* <StyledAccountFooter>
            <FontAwesomeIcon icon={faShieldHalved} />
            <span>Your account is protected</span>
          </StyledAccountFooter> */}

        </StyledAccountContent>
      )}

    </StyledAccountSectionContainer>
  );
});

AccountSection.displayName = 'AccountSection';

export default AccountSection;
